import { evalDistroConditional } from '@aidkitorg/types/lib/eval';
import * as v0 from '@aidkitorg/types/lib/survey';
import { useContext } from 'react';
import InterfaceContext from '../Context';
import { InfoDict } from '../Questions/Props';
import { useMarkdown } from '../Util';

/**
 * - Converts Content (Array or Object or String) to a single Markdown JSX Element
 * - Replaces any $variables with the value of props.info[variable]
 */
export function useModularMarkdown(props: {
    content: string | v0.Content, 
    info: InfoDict,
    uid?: string,
    replacer?: (str: string) => string,
    collapsible?: boolean, 
    noParagraphs?: boolean
}) {
    const context = useContext(InterfaceContext);

    let { content, info, collapsible, noParagraphs } = props;

    const regex = /\$[a-zA-Z\\_][a-zA-Z\\_0-9]+/g;
    function infoReplacer(match: string){ 
        const variable = match.slice(1).replace(/\\_/g,'_');
        if(variable === "uid") return props.uid || '';
        if(props.info[variable] !== undefined && props.info[variable] !== null) return props.info[variable] || '';
        return match;
    }

    let firstReplace = (str: string) => str.replace(regex, infoReplacer);
    let replacer = props.replacer ? (str: string) => props.replacer!(firstReplace(str)) : firstReplace;

    let markedPieces: string[] = [];
    if (Array.isArray(content)) {
        type T = v0.RichText | v0.ConditionalContent;
        type C = v0.ConditionalContent;
        let addContent = (content: T) => {
            if ((content as C).conditional) {
                // Map components
                let passing = evalDistroConditional((content as C).conditional, info);
                if (passing) {
                    let components = (content as C).components;
                    if (Array.isArray(components)) {
                        for (const component of components) {
                            addContent(component);
                        }
                    } else {
                        addContent(components);
                    }
                } else {
                    let otherwise = (content as C).otherwise;
                    if (otherwise) {
                        if (Array.isArray(otherwise)) {
                            for (const component of otherwise) {
                                addContent(component);
                            }
                        } else {
                            addContent(otherwise);
                        }
                    }
                }
            } else {
                let text = content as v0.RichText;
                markedPieces.push(text[context.lang as keyof v0.Text] || text.en || '');
            }
        }
        content.map(addContent);
    } else {
        markedPieces = [typeof content === 'string' ? content : content[context.lang as keyof v0.Text] || content.en || ''];
    }

    return useMarkdown(replacer(markedPieces.join("\n")), collapsible, noParagraphs);
  }